import { useParams, Link } from 'react-router-dom';
import { articles, comments } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleContent from './components/ArticleContent';
import CommentSection from './components/CommentSection';
import RelatedArticles from './components/RelatedArticles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">这篇爆料可能已经被删除了</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // JSON-LD Structured Data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜爆料网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51chigua.com/article/${article.id}`,
    },
    image: article.coverImage,
    articleSection: article.category,
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://51chigua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://51chigua.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      <Navbar />

      <main>
        {/* Article Header Hero */}
        <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70"></div>

          <div className="relative z-10 w-full h-full flex flex-col justify-end px-4 md:px-6 pb-10 md:pb-14">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-background-200/80 text-xs mb-4 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-background-50 transition-colors cursor-pointer">{article.category}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-50">{article.title.slice(0, 15)}...</span>
              </nav>

              {/* Category Tag */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium mb-4">
                <i className="ri-fire-line"></i>
                <span>{article.category}</span>
              </div>

              {/* Title */}
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight mb-3">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="flex items-center gap-4 text-background-200/70 text-xs">
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  <span>{article.publishDate}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  <span>{article.heat > 10000 ? `${(article.heat / 10000).toFixed(1)}万` : article.heat.toLocaleString()} 热度</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <ArticleContent article={article} />

        {/* Comments */}
        <CommentSection articleId={article.id} />

        {/* Related Articles */}
        <RelatedArticles currentId={article.id} category={article.categorySlug} />
      </main>

      <Footer />
    </>
  );
}